/**
 * useBulkSelection — Generic reusable hook for Set-based item selection.
 * Survives pagination (Set<string> persists across page changes).
 *
 * @package @kgc/web
 * Epic 5: Bulk Email (ADR-090)
 */

import { useCallback, useState } from 'react';

export interface BulkSelectionReturn {
  /** Currently selected IDs */
  selectedIds: Set<string>;
  /** Number of selected items */
  selectedCount: number;
  /** Toggle a single item */
  toggleSelect: (id: string) => void;
  /** Toggle all items on the current page */
  toggleSelectAll: (currentPageIds: string[]) => void;
  /** Check if an item is selected */
  isSelected: (id: string) => boolean;
  /** Check if all items on current page are selected */
  isAllSelected: (currentPageIds: string[]) => boolean;
  /** Clear all selections */
  clearSelection: () => void;
}

export function useBulkSelection(): BulkSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((currentPageIds: string[]) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = currentPageIds.every(id => next.has(id));
      if (allSelected) {
        currentPageIds.forEach(id => next.delete(id));
      } else {
        currentPageIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const isAllSelected = useCallback(
    (currentPageIds: string[]) =>
      currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.has(id)),
    [selectedIds]
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    toggleSelect,
    toggleSelectAll,
    isSelected,
    isAllSelected,
    clearSelection,
  };
}
